import type { TemplatePurpose } from '@brush/api-client';

/**
 * The players template in the dialog (ADR 0021, docs/specs/player-import.md PI65–PI68, PI80).
 *
 * The server is the control — `TemplateService` refuses a changed field set, a label naming another column, a second
 * players template, a purpose moved to or from it, and a delete. This only keeps the dialog from offering what the
 * server would refuse, so an administrator is not left to find out by saving.
 */
export const PLAYERS_PURPOSE: TemplatePurpose = 'import_players';

export interface TemplateControls {
  /** The seven fields, in order, with their flags and type: only labels and notes are editable. */
  fieldSetLocked: boolean;
  /** The purpose is shown, not offered: the players template stays one (PI68). */
  purposeFixed: boolean;
  /** No feature gate: every room fills the players template in (PI66). */
  featuresLocked: boolean;
  /** Whether Delete is offered: never on the players template (PI80), and never before it exists. */
  deletable: boolean;
}

/**
 * PI65's seven, which a new players template starts with. The server holds the same list (`PlayersTemplate.Columns`)
 * and refuses any other; this copy only seeds the form.
 */
export const PLAYERS_FIELDS: { key: string; label: string; required: boolean }[] = [
  { key: 'CardID', label: 'CardID', required: true },
  { key: 'FirstName', label: 'FirstName', required: true },
  { key: 'LastName', label: 'LastName', required: true },
  { key: 'City', label: 'City', required: false },
  { key: 'State', label: 'State', required: false },
  { key: 'Email', label: 'Email', required: false },
  { key: 'Phone', label: 'Phone', required: false },
];

/** What the dialog offers, given what it was opened to do and the purpose in hand (stored, or chosen on a create). */
export function templateControls(
  mode: 'create' | 'clone' | 'edit',
  purpose: TemplatePurpose,
): TemplateControls {
  const players = purpose === PLAYERS_PURPOSE;
  return {
    fieldSetLocked: players,
    purposeFixed: players && mode === 'edit',
    featuresLocked: players,
    deletable: mode === 'edit' && !players,
  };
}

/**
 * The purposes to offer, from those the kind allows (`purposesFor`): the players template keeps its own and no other,
 * no other template is offered it, and a new one is offered it only while none exists (PI68).
 */
export function purposeChoices(
  choices: readonly TemplatePurpose[],
  {
    mode,
    storedPurpose,
    playersTemplateExists,
  }: {
    mode: 'create' | 'clone' | 'edit';
    storedPurpose: TemplatePurpose | null;
    playersTemplateExists: boolean;
  },
): TemplatePurpose[] {
  if (mode === 'edit') {
    return storedPurpose === PLAYERS_PURPOSE
      ? choices.filter((p) => p === PLAYERS_PURPOSE)
      : choices.filter((p) => p !== PLAYERS_PURPOSE);
  }

  return playersTemplateExists ? choices.filter((p) => p !== PLAYERS_PURPOSE) : [...choices];
}

/**
 * The dialog's line under its title, about room content. Locked rule 9 holds for every template but this one: a room
 * that has not filled in the players template still imports, reading every column (PI71).
 */
export function contentLine(purpose: TemplatePurpose): string {
  return purpose === PLAYERS_PURPOSE
    ? 'Rooms supply their own content for this. A room that hasn’t filled it in reads every column.'
    : 'Rooms supply their own content for this. A template no room has filled in cannot be used by that room.';
}

/**
 * The fields' legend. On the players template the helper under *Used for* already says what its columns are and what
 * may change, so the legend is kept for screen readers only rather than saying it a second time.
 */
export function fieldsLegend(fieldSetLocked: boolean): { text: string; visuallyHidden: boolean } {
  return fieldSetLocked
    ? { text: 'Fields', visuallyHidden: true }
    : {
        text: 'Fields a room’s CSV may carry. Required ones it cannot leave out; optional ones it may omit. This order is the starting order rooms see.',
        visuallyHidden: false,
      };
}
